import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, ChevronRight, MessageSquareQuote } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { easerGet } from '../../lib/easerApi';
import { QualityDetailDialog } from './qualite/QualityDetailDialog';
import { StarRating, fmtAvg, fmtDay } from './qualite/qcShared';

/** Accueil : contrôles qualité reçus par l'Easer (nouveaux en évidence), ouverture du détail + réponse. */
export function EaserQualityCard({ user, onUnauthorized }) {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await easerGet('/api/easer/qualite', { limit: 20 });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
      else setData({ items: [], unread: 0, mine: { count: 0 } });
    }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Skeleton className="h-16 rounded-xl" />;
  const mine = (data.items || []).filter((c) => (c.easer_ids || []).includes(user?.id));
  if (mine.length === 0) return null; // rien à montrer tant qu'aucun contrôle ne concerne cet Easer

  return (
    <Card className="p-4 shadow-sm border-border" data-testid="easer-quality-card">
      <div className="flex items-center gap-3">
        <span className="h-11 w-11 rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0"><ClipboardCheck className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Contrôles qualité</p>
          <p className="text-sm"><span className="font-semibold" data-testid="easer-quality-count">{data.mine?.count || mine.length}</span> contrôle{mine.length > 1 ? 's' : ''} · moyenne <span className="font-semibold" data-testid="easer-quality-average">{fmtAvg(data.mine?.average)}</span> / 5</p>
        </div>
        {data.unread > 0 && <Badge className="bg-destructive text-white border-0" data-testid="easer-quality-unread">{data.unread} nouveau{data.unread > 1 ? 'x' : ''}</Badge>}
      </div>
      <ul className="mt-3 divide-y divide-border rounded-xl border border-border" data-testid="easer-quality-list">
        {mine.slice(0, 3).map((c) => (
          <li key={c.id}>
            <button type="button" onClick={() => setDetail(c.id)} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-secondary/60 min-h-[44px]" data-testid={`easer-quality-item-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className={`text-sm leading-snug truncate ${c.unread ? 'font-semibold' : ''}`}>{c.label}{c.client_name ? ` · ${c.client_name}` : ''}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">{fmtDay(c.created_at)} · {c.manager_name}{c.reply ? <span className="inline-flex items-center gap-0.5"><MessageSquareQuote className="h-3 w-3" /> répondu</span> : c.unread ? <span className="text-destructive font-medium">nouveau</span> : null}</p>
              </div>
              <StarRating value={c.average} size="h-3.5 w-3.5" />
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          </li>
        ))}
      </ul>
      <QualityDetailDialog open={!!detail} controlId={detail} userId={user?.id} onClose={() => setDetail(null)} onUnauthorized={onUnauthorized} onChanged={() => load()} />
    </Card>
  );
}
